<!--
  InboundPoolGauge.vue
  콜봇 월 정산 · 정산 탭 오른쪽 '포함 120분' 카드 — 월 사용료에 든 포함 통화를 이 달 얼마나 썼는지(목업 Detail.dc.html aside).

  요청: ~/md/front/admin-콜봇-월정산-call-api-구현회신.md "2026-10-16 개정 — 요금표 v70" 4(`charge.lines.inbound`)·5
        (게이지 = (`usage.inbound.units_10s` + `usage.web_voice.units_10s`) ÷ `charge.lines.inbound.included_units_10s`)
  - 막대는 포함 단위까지 채우고, 넘으면 끝까지 채운 뒤 세 칸(사용 / 포함 / 초과 또는 남음)의 초과를 경고색으로 적는다. 칸은 분
    표기만 보인다 — 10초 단위 수('n단위')는 화면에 쓰지 않는다. 포함 단위는 개통 달이면 일할해 올린 값이다(제목 옆 '개통 달 n/m일').
  - 사용 = 전화(수신 — 통화마다 AI + 상담사 통화를 더해 10초 올림) + 웹 음성. 그 나눔·수신 통화 수·시간·통화에 붙지 않은
    상담사 연결 수는 아래 한 줄 끝의 툴팁에.
  - 기본 요율이 없는 달은 포함 단위를 몰라 사용량만 보인다. v70 이전 정산서(`statement.legacy`)와 사용량이 예전 모양인
    응답은 그리지 않는다(settlementRules.inboundPoolView 가 null).
  - 정산 시작 월(1일 아님 — `charge.lines.base.start_month`)이면 포함 단위도 시작일부터 일할한 값이라 칩이 '시작 월 n/m일'
    (개통 달 칩 자리, 시작 월이 우선). 칸이 없으면 지금 그대로.
  데이터: 정산 상세 하나(부모가 넘긴다). 아무것도 부르지 않는다.
-->
<template>
  <section v-if="view" class="pool-card" :class="{ 'is-over': view.over }" :aria-labelledby="labelId">
    <div class="pool-card__head">
      <h2 :id="labelId" class="pool-card__title">{{ titleText }}</h2>
      <span v-if="startMonthText" class="pool-card__chip">시작 월 {{ startMonthText }}</span>
      <span v-else-if="view.opening" class="pool-card__chip">개통 달 {{ openingText }}</span>
      <span v-if="isNumber(view.percent)" class="pool-card__percent">{{ formatNumber(view.percent) }}%</span>
    </div>

    <div
      v-if="isNumber(view.included)"
      class="pool-card__track"
      role="meter"
      :aria-labelledby="labelId"
      aria-valuemin="0"
      :aria-valuemax="view.included"
      :aria-valuenow="Math.min(view.used, view.included)"
      :aria-valuetext="valueText"
    >
      <span class="pool-card__bar" :style="{ width: `${barPercent}%` }"></span>
    </div>

    <dl class="pool-card__cells">
      <div>
        <dt>사용</dt>
        <dd>{{ view.usedText }}</dd>
      </div>
      <div v-if="isNumber(view.included)">
        <dt>포함</dt>
        <dd>{{ view.includedText }}</dd>
      </div>
      <div v-if="view.over">
        <dt>초과</dt>
        <dd class="is-over">{{ view.overageText }}</dd>
      </div>
      <div v-else-if="isNumber(view.included)">
        <dt>남음</dt>
        <dd>{{ leftText }}</dd>
      </div>
    </dl>

    <p class="pool-card__sub">
      {{ isNumber(view.included) ? '수신 통화의 AI + 상담사 통화와 웹 음성 · 통화마다 10초 올림' : '기본 요율이 없어 포함 통화를 모릅니다.' }}
      <a-tooltip :title="breakdownText" :trigger="['hover', 'focus']">
        <button type="button" class="pool-card__info" :aria-label="`사용량 나눔: ${breakdownText}`">
          <InfoCircleOutlined aria-hidden="true" />
        </button>
      </a-tooltip>
    </p>
  </section>
</template>

<script setup>
import { computed, getCurrentInstance } from 'vue';
import { InfoCircleOutlined } from '@ant-design/icons-vue';

import { formatDuration, formatNumber, isNumber } from '../../service/settlementFormat';
import { inboundPoolView, unitsMinutesText } from '../../service/settlementRules';

const props = defineProps({
  /** 정산 상세 응답(요청 계약 2) */
  detail: { type: Object, required: true },
});

const labelId = `st-pool-gauge-${getCurrentInstance()?.uid ?? 0}`;

const view = computed(() => inboundPoolView(props.detail));

const titleText = computed(() => `포함 ${formatNumber(view.value?.includedMinutes ?? 120)}분`);

const openingText = computed(() => {
  const base = props.detail.charge?.lines?.base;
  return isNumber(base?.days) && isNumber(base?.days_in_month)
    ? `${formatNumber(base.days)}/${formatNumber(base.days_in_month)}일`
    : '일할';
});

/** 시작 월 일할 'n/m일' — start_month 칸이 없거나 모양이 틀리면 '' */
const startMonthText = computed(() => {
  const sm = props.detail.charge?.lines?.base?.start_month;
  if (!sm || !isNumber(sm.days) || !isNumber(sm.month_days)) return '';
  return `${formatNumber(sm.days)}/${formatNumber(sm.month_days)}일`;
});

const barPercent = computed(() => {
  const v = view.value;
  if (!v || !isNumber(v.included) || v.included <= 0) return v?.used > 0 ? 100 : 0;
  return Math.min(100, Math.round((v.used * 1000) / v.included) / 10);
});

const left = computed(() => {
  const v = view.value;
  if (!v || !isNumber(v.included)) return null;
  return Math.max(0, v.included - v.used);
});

const leftText = computed(() => (isNumber(left.value) ? unitsMinutesText(left.value) : ''));

const valueText = computed(() => {
  const v = view.value;
  if (!v) return '';
  const head = `사용 ${v.usedText}, 포함 ${v.includedText}`;
  return v.over ? `${head}, 초과 ${v.overageText}` : `${head}, 남음 ${leftText.value}`;
});

const breakdownText = computed(() => {
  const v = view.value;
  if (!v) return '';
  const parts = [`전화 ${unitsMinutesText(v.phone)} + 웹 음성 ${unitsMinutesText(v.webVoice)} = ${v.usedText}`];
  if (isNumber(v.calls)) {
    const times = [
      isNumber(v.aiSeconds) ? `AI ${formatDuration(v.aiSeconds)}` : '',
      isNumber(v.talkSeconds) ? `상담사 ${formatDuration(v.talkSeconds)}` : '',
    ].filter(Boolean);
    parts.push(`수신 ${formatNumber(v.calls)}통${times.length ? `(${times.join(' + ')})` : ''}`);
  }
  if (isNumber(v.unmatchedLegs) && v.unmatchedLegs > 0) {
    parts.push(`통화에 붙지 않은 상담사 연결 ${formatNumber(v.unmatchedLegs)}건 포함`);
  }
  return parts.join(' · ');
});
</script>

<style scoped lang="scss">
@use '../../styles/settlement' as *;

.pool-card {
  @include st-card;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px 20px;
  color: $st-text;

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 10px;
  }

  &__title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
  }

  &__chip {
    @include st-pill;
  }

  &__percent {
    @include st-num;
    margin-left: auto;
    font-size: 15px;
    font-weight: 700;
  }

  &__track {
    height: 10px;
    overflow: hidden;
    border-radius: 999px;
    background: $st-border;
  }

  &__bar {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: $st-accent;
  }

  &.is-over &__bar {
    background: $st-warning;
  }

  &.is-over &__percent {
    color: $st-warning;
  }

  &__cells {
    display: grid;
    // A long value ('1,606분 20초') wraps inside its own cell instead of running into the next one
    grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
    gap: 8px;
    margin: 0;

    div {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    dt {
      font-size: 12px;
      font-weight: 400;
      color: $st-muted;
    }

    dd {
      margin: 0;
      font-size: 15px;
      font-weight: 600;
      font-variant-numeric: tabular-nums;
      white-space: normal;
      word-break: keep-all;

      &.is-over {
        color: $st-warning;
      }
    }
  }

  &__sub {
    margin: 0;
    font-size: 12.5px;
    line-height: 1.5;
    color: $st-muted;
  }

  &__info {
    @include st-info-btn;
  }
}
</style>
